import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Button, message } from 'antd';
import { approveMaintenance, rejectMaintenance, commitMaintenanceDiffs } from '../../../../services/api';
import { DiffAction } from '../../../../models/maintenance';
import ModalFiltersDrawer from './ModalFiltersDrawer';
import styles from './MXDraftQueue.module.less';

const ListTitle = ({
  aircraft,
  mxItems,
  filters,
  title,
  onFilterChange,
  onFiltersClear,
  intl: { formatMessage },
  onSuccess,
  handleLoadingChange,
  submitting,
}) => {
  const itemNumber = mxItems.length;
  const draftItems = mxItems.filter((item) => item.draft);
  const campUpdateItems = mxItems.filter((item) => item.camp_diffs && item.camp_diffs.length > 0);

  const handleDraftItems = async (draftHandler) => {
    if (draftItems.length === 0) {
      return [];
    }

    const draftIds = draftItems.map((item) => item.id);
    const draftResponse = await draftHandler({ ids: draftIds });

    return draftResponse && draftResponse.statusCode === 204 ? draftItems : [];
  };

  const handleCampUpdates = async (action) => {
    if (campUpdateItems.length === 0) {
      return [];
    }

    const updates = campUpdateItems.map(async (item) => {
      const result = await commitMaintenanceDiffs({
        id: item.id,
        commits: item.camp_diffs.map((diff) => ({
          ...diff,
          action,
        })),
      });

      return result && result.statusCode === 200 ? item : null;
    });

    const results = await Promise.all(updates);
    return results.filter((item) => item !== null);
  };

  const handleChanges = async ({
    draftHandler,
    campUpdateAction,
    successMessageId,
    errorMessageId,
  }) => {
    try {
      handleLoadingChange();

      const processedDraftItems = await handleDraftItems(draftHandler);
      const processedUpdateItems = await handleCampUpdates(campUpdateAction);
      const processedItems = [...processedDraftItems, ...processedUpdateItems];

      if (processedItems.length > 0) {
        message.success(formatMessage({ id: successMessageId }, { field: processedItems.length }));

        // No need to return items when everything processes since this will close the review modal
        if (processedItems.length === itemNumber) {
          onSuccess();
        } else {
          // throwing an error on partial success so the client knows it didn't fully succeed
          message.error(formatMessage({ id: errorMessageId }));
          onSuccess(processedItems);
        }
      } else {
        message.error(formatMessage({ id: errorMessageId }));
      }
    } catch (error) {
      message.error(formatMessage({ id: errorMessageId }));
    } finally {
      handleLoadingChange();
    }
  };

  const acceptAllItems = async () => {
    await handleChanges({
      draftHandler: approveMaintenance,
      campUpdateAction: DiffAction.MERGE,
      successMessageId: 'message.approveMultiItemsSuccess',
      errorMessageId: 'message.approveItemsError',
    });
  };

  const rejectAllItems = async () => {
    await handleChanges({
      draftHandler: rejectMaintenance,
      campUpdateAction: DiffAction.REJECT,
      successMessageId: 'message.rejectMultiItemsSuccess',
      errorMessageId: 'message.rejectItemsError',
    });
  };

  return (
    <>
      <div className={styles.listTitle}>
        <div className={styles.title} data-test="title">
          {title}
        </div>
        <div className={styles.buttonWrapper}>
          {mxItems && mxItems.length > 1 ? (
            <div className={styles.allButtons}>
              <Button
                type="link"
                onClick={() => acceptAllItems()}
                className={styles.acceptButton}
                data-test="acceptButton"
                disabled={submitting}
              >
                {formatMessage({ id: 'text.acceptAll' })}
              </Button>
              <Button
                type="link"
                onClick={() => rejectAllItems()}
                className={styles.rejectButton}
                data-test="rejectButton"
                disabled={submitting}
              >
                {formatMessage({ id: 'text.rejectAll' })}
              </Button>
            </div>
          ) : null}
          <div className={styles.filtersDrawerWrapper}>
            <ModalFiltersDrawer
              aircraft={aircraft}
              mxItems={mxItems}
              filters={filters}
              onFilterChange={(filterKey, groupKey) => onFilterChange(filterKey, groupKey)}
              onFiltersClear={(groupKey) => onFiltersClear(groupKey)}
            />
          </div>
        </div>
      </div>
      <div className={styles.listTitleMobile}>
        <div className={styles.titleWrapper}>
          <div className={styles.title} data-test="title">
            {title}
            {` (${mxItems.length})`}
          </div>
          <div className={styles.filtersDrawerWrapper}>
            <ModalFiltersDrawer
              aircraft={aircraft}
              mxItems={mxItems}
              filters={filters}
              onFilterChange={(filterKey, groupKey) => onFilterChange(filterKey, groupKey)}
              onFiltersClear={(groupKey) => onFiltersClear(groupKey)}
            />
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <div className={styles.allButtons}>
            <Button
              type="link"
              onClick={() => acceptAllItems()}
              className={styles.acceptButton}
              data-test="acceptButton"
              disabled={submitting}
            >
              {formatMessage({ id: 'text.acceptAll' })}
            </Button>
            <Button
              type="link"
              onClick={() => rejectAllItems()}
              className={styles.rejectButton}
              data-test="rejectButton"
              disabled={submitting}
            >
              {formatMessage({ id: 'text.rejectAll' })}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

ListTitle.propTypes = {
  title: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onFiltersClear: PropTypes.func.isRequired,
  aircraft: PropTypes.object.isRequired,
  mxItems: PropTypes.array.isRequired,
  filters: PropTypes.object.isRequired,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }).isRequired,
  onSuccess: PropTypes.func.isRequired,
  handleLoadingChange: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
};

export default injectIntl(ListTitle);
